import cn from 'classnames'
import { type ReactNode, useEffect, useId, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { TranslationMode } from 'cng-common/features/translations/configs'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Tooltip } from 'core-components/library/tooltip'
import { useCurrencyRestriction } from 'partner360/components/currenciesButtons/hooks/useCurrencyRestriction'
import { CURRENCY_TRANSLATION_KEYS } from 'partner360/components/currenciesButtons/translations'

import {
  SMB_GUIDED_GROWTH_MAX,
  SMB_GUIDED_GROWTH_MIN,
  SMB_GUIDED_GROWTH_STEP,
} from '../../../configs/guidedSetup'
import {
  IPlanningSmbGuidedTranslation,
  SMB_GUIDED_WIZARD_TEXT as TEXT,
} from '../../../configs/guidedWizardTranslations'
import { TPlanningSmbGuidedWizard } from '../../../hooks/usePlanningSmbGuidedWizard'
import { IPlanningSmbGuidedWizardProps } from '../../../model/guidedWizardTypes'
import styles from '../PlanningSmbGuidedWizard.module.scss'

import {
  PlanningSmbGuidedText as Text,
  usePlanningSmbGuidedTranslation,
} from './PlanningSmbGuidedText'

export const PlanningSmbGuidedGrowth = ({
  editor,
  compact = false,
}: {
  editor: TPlanningSmbGuidedWizard
  compact?: boolean
}) => {
  const translate = usePlanningSmbGuidedTranslation()
  const [input, setInput] = useState(String(editor.draft.growth))
  useEffect(() => setInput(String(editor.draft.growth)), [editor.draft.growth])
  return (
    <div className={cn(styles.stepper, { [styles.stepperCompact]: compact })}>
      <button
        type="button"
        aria-label={translate(TEXT.decrease)}
        disabled={editor.isApplying || editor.draft.growth <= SMB_GUIDED_GROWTH_MIN}
        onClick={() => editor.setGrowth(editor.draft.growth - SMB_GUIDED_GROWTH_STEP)}
      >
        −
      </button>
      <span className={styles.growthInput}>
        <input
          type="number"
          min={SMB_GUIDED_GROWTH_MIN}
          max={SMB_GUIDED_GROWTH_MAX}
          step={SMB_GUIDED_GROWTH_STEP}
          value={input}
          aria-label={translate(TEXT.growth)}
          disabled={editor.isApplying}
          onChange={(event) => {
            setInput(event.target.value)
            if (event.target.value !== '' && Number.isFinite(event.target.valueAsNumber))
              editor.setGrowth(event.target.valueAsNumber)
          }}
          onBlur={() => {
            editor.setGrowth(Number(input))
            setInput(String(editor.draft.growth))
          }}
        />
        <span className={styles.unit}>
          <Text text={TEXT.points} />
        </span>
      </span>
      <button
        type="button"
        aria-label={translate(TEXT.increase)}
        disabled={editor.isApplying || editor.draft.growth >= SMB_GUIDED_GROWTH_MAX}
        onClick={() => editor.setGrowth(editor.draft.growth + SMB_GUIDED_GROWTH_STEP)}
      >
        +
      </button>
    </div>
  )
}

const Segments = ({
  label,
  value,
  options,
  disabled,
  onChange,
  disabledTooltip,
}: {
  label: IPlanningSmbGuidedTranslation
  value: string
  options: { value: string; text?: IPlanningSmbGuidedTranslation }[]
  disabled: boolean
  onChange: (value: string) => void
  disabledTooltip?: ReactNode
}) => {
  const translate = usePlanningSmbGuidedTranslation()
  const control = (
    <div className={styles.segments} role="group" aria-label={translate(label)}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={cn({ [styles.segmentSelected]: value === option.value })}
          disabled={disabled}
          onClick={() => {
            if (!disabled) onChange(option.value)
          }}
        >
          {option.text ? <Text text={option.text} /> : option.value}
        </button>
      ))}
    </div>
  )
  return disabled && disabledTooltip ? (
    <Tooltip inline tipContent={() => <>{disabledTooltip}</>}>
      {control}
    </Tooltip>
  ) : (
    control
  )
}

export const PlanningSmbGuidedControls = ({
  props,
  editor,
}: {
  props: IPlanningSmbGuidedWizardProps
  editor: TPlanningSmbGuidedWizard
}) => {
  const translationMode = useSelector(selectTranslationMode)
  const switchLabelId = useId()
  const currencyRestriction = useCurrencyRestriction(editor.currency, editor.setCurrency)
  return (
    <div className={styles.toolbar}>
      <div className={styles.growthStrip}>
        <span className={styles.controlTitle}>
          <Text text={TEXT.growth} />
        </span>
        <PlanningSmbGuidedGrowth editor={editor} compact />
        <span className={styles.growthNote}>
          <Text text={TEXT.fillsUnchanged} />
        </span>
      </div>
      <div className={styles.displayControls}>
        <div className={styles.financialSwitch}>
          <button
            type="button"
            role="switch"
            aria-checked={editor.showFinancialData}
            aria-labelledby={switchLabelId}
            className={styles.switch}
            disabled={editor.isApplying}
            onClick={() => editor.setShowFinancialData(!editor.showFinancialData)}
          />
          <span
            id={switchLabelId}
            onClick={
              translationMode !== TranslationMode.Edit && !editor.isApplying
                ? () => editor.setShowFinancialData(!editor.showFinancialData)
                : undefined
            }
          >
            <Text text={TEXT.financial} />
          </span>
        </div>
        <Segments
          label={TEXT.metric}
          value={editor.metric}
          disabled={editor.isApplying}
          options={props.actualsTypes.map((value) => ({
            value,
            text: {
              key: `jbp-partner-actuals-value-type-${value.toLowerCase()}`,
              fallback: value === 'Ndp' ? 'NDP' : value,
            },
          }))}
          onChange={(value) => editor.setMetric(value as typeof editor.metric)}
        />
        <Segments
          label={TEXT.currency}
          value={currencyRestriction.currency}
          disabled={editor.isApplying || currencyRestriction.isMultiLocalCurrencyLocked}
          options={(currencyRestriction.isMultiLocalCurrencyLocked &&
          !props.currencies.includes('USD')
            ? ['USD', ...props.currencies]
            : props.currencies
          ).map((value) => ({ value }))}
          onChange={currencyRestriction.changeCurrency}
          disabledTooltip={
            currencyRestriction.isMultiLocalCurrencyLocked ? (
              <Translation text={CURRENCY_TRANSLATION_KEYS.multipleLocalCurrencies} />
            ) : undefined
          }
        />
        <Segments
          label={TEXT.scale}
          value={props.roundingValue}
          disabled={editor.isApplying}
          options={props.roundingOptions.map((value) => ({
            value,
            text: value === 'AsIs' ? TEXT.asIs : undefined,
          }))}
          onChange={props.onRoundingChange}
        />
      </div>
    </div>
  )
}
